import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import bubble from '../../assets/images/bubble.png';
import bubblePop1 from '../../assets/images/bubble-pop-1.png';
import bubblePop2 from '../../assets/images/bubble-pop-2.png';
import bubblePop3 from '../../assets/images/bubble-pop-3.png';
import bubblePop4 from '../../assets/images/bubble-pop-4.png';
import { colors, fonts } from '../../theme';

gsap.registerPlugin(ScrollTrigger);

// Answers marked "TODO" below are placeholders — swap in the real details.
// An answer can have an intro line (`a`), a bulleted list, and a closing note.
type FaqEntry = {
  q: string;
  a?: string;
  // A bullet can be plain text, or text with a nested sub-list.
  bullets?: (string | { text: string; sub: string[] })[];
  note?: string;
  // Optional link shown at the end of the answer.
  link?: { label: string; href: string };
};

const registration: FaqEntry[] = [
  {
    q: 'How do I submit my project?',
    a: 'Submissions go through Devpost.',
    bullets: [
      'Initial submission is due Sunday at 11:00 AM. A placeholder is fine — we use it to record teams, assign table numbers, and set up judging.',
      'Final submission is due Sunday at 12 noon. No changes are allowed after that.',
      'Keep at least 2 hours at the end to record your video and write up. Don’t rush it.',
    ],
  },
  {
    q: 'Who can participate?',
    a: 'SanD Hacks welcomes beginners and experienced developers alike. Use the Register button at the top of the page to apply and we’ll follow up with next steps.',
  },
  {
    q: 'Is there a code cutoff for the hackathon?',
    a: 'Yes. Your project can only have commits during the event:',
    bullets: ['Not before Saturday at 10:30 AM', 'Not after Sunday at 12 noon'],
    note: 'Commits outside that window mean immediate disqualification.',
  },
  {
    q: 'What’s included in a submission?',
    a: 'Every submission needs:',
    bullets: [
      '3-minute video: your idea, a demo (the most important part!), and the tech you used. Upload it as a public YouTube or Google Drive link.',
      'Short write-up answering Devpost’s prompts: problem, solution, challenges, and tools used.',
      'A GitHub repo or Lovable project link, plus a live demo link if you have one.',
    ],
  },
];

const general: FaqEntry[] = [
  {
    q: 'What is SanD Hacks?',
    a: 'SanD Hacks is UC San Diego’s premier student-run hackathon, bringing together innovative minds to build, create, and collaborate.',
  },
  {
    q: 'Is this beginner friendly?',
    a: 'Absolutely. Whether you’re a beginner or an experienced developer, you’ll find your place here — there are technical workshops and one-on-one mentoring sessions throughout the event.',
  },
  {
    q: 'Will there be workshops?',
    a: 'Yes! Saturday includes technical workshops and one-on-one mentoring sessions all day.',
  },
  {
    q: 'What are the prizes?',
    a: 'Prize details will be announced soon — check back closer to the event.', // TODO
  },
  {
    q: 'Can I use AI tools or the internet?',
    a: 'This is an open-AI and open-internet hackathon. Feel free to use any available resources for building your project.',
  },
  {
    q: 'Is there a code of conduct?',
    a: 'Yes. SanD Hacks is an MLH member event, and everyone attending is expected to follow the MLH Code of Conduct.',
    link: {
      label: 'Read the MLH Code of Conduct',
      href: 'https://github.com/MLH/mlh-policies/blob/main/code-of-conduct.md',
    },
  },
  {
    q: 'How does judging work?',
    a: 'Judging is in person from 1–3 PM, science-fair style.',
    bullets: [
      'Every project gets a table number — your whole team must be there, or the judge gives a zero and moves on. Each judge visit is 3 minutes to present plus 2 minutes of questions.',
      'Judging is holistic: idea, demo, technical complexity, and completion.',
      'Code quality (comments, file structure) isn’t scored.',
    ],
  },
];

const logistics: FaqEntry[] = [
  {
    q: 'What should I bring?',
    a: 'Packing list:',
    bullets: [
      'University/government ID (mandatory), plus your laptop and chargers.',
      'Headphones and a sweater or jacket — it gets cold.',
      'Water bottle, snacks, and deodorant.',
    ],
  },
  {
    q: 'Will there be food?',
    a: 'Yes — snacks keep you going during Midnight Activities on Saturday. More details on meals will be shared closer to the event.', // TODO
  },
  {
    q: 'When/Where is SanD Hacks?',
    a: 'SanD Hacks takes place November 14–15, 2026 at UC San Diego. Exact venue details will be sent to registered participants.',
  },
  {
    q: 'When is check-in?',
    a: 'Exact check-in times will be emailed to registered participants before the event.', // TODO
  },
  {
    q: 'Do I need to be present the entire time?',
    a: 'We encourage you to stay for the whole event, since workshops, mentoring, and judging happen throughout. Let an organizer know if you need to step away.', // TODO
  },
  {
    q: 'When does the event end?',
    a: 'The event wraps up around 4:00 PM with the awards ceremony.',
  },
  {
    q: 'Who do I contact if I have questions or an emergency during the event?',
    a: 'Find any organizer at the event. Contact details will be shared with registered participants beforehand.', // TODO
  },
];

// Card geometry, taken from the design (cards are 232px wide).
const CARD_W = 232;
// How far below the panel's current (growing) bottom edge the bubble field starts — a fixed
// px depth, the same for every bubble regardless of its own size, so it's one clear knob
// rather than each bubble's own transform deciding it.
const BUBBLE_START_DEPTH = 15;
// How tall the fade zone is at the panel's growing edge (both the cream-to-periwinkle
// background gradient and the text mask share this, so they line up).
const EDGE_FADE_LENGTH = 60;
// The answer-opening bubble field's density: 200 bubbles looked right on the "When is
// check-in?" answer (69px of content), so that pairing is the reference — every other answer
// gets a bubble count scaled to its own height off the same density, instead of a flat count
// that would look sparse on tall answers or absurdly dense on short ones.
const BUBBLE_REFERENCE_HEIGHT = 69;
const BUBBLE_REFERENCE_COUNT = 200;
const BUBBLE_DENSITY = BUBBLE_REFERENCE_COUNT / BUBBLE_REFERENCE_HEIGHT;

// A decorative bubble that rises on a loop and can be popped by clicking it: it bulges and
// vanishes, then respawns (restarting its rise from the bottom) after a short random pause.
// Position, pop and rise live on three nested elements so their animations never fight.
const POP_MS = 320;
const popImages = [bubblePop1, bubblePop2, bubblePop3, bubblePop4];
export function PoppableBubble({
  sx,
  rise,
  display = { xs: 'none', md: 'block' },
}: {
  sx: Record<string, unknown>;
  rise: Record<string, unknown>;
  display?: Record<string, string> | string;
}) {
  const [state, setState] = useState<'idle' | 'popping' | 'gone'>('idle');
  const [spawn, setSpawn] = useState(0);
  const [popImg, setPopImg] = useState(bubblePop1);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const pop = () => {
    if (state !== 'idle') return;
    setPopImg(popImages[Math.floor(Math.random() * popImages.length)]);
    setState('popping');
    timers.current.push(
      setTimeout(() => {
        setState('gone');
        timers.current.push(
          setTimeout(() => {
            setSpawn((n) => n + 1); // remount the img so the rise animation restarts
            setState('idle');
          }, 300 + Math.random() * 700),
        );
      }, POP_MS),
    );
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        display,
        zIndex: 3,
        pointerEvents: 'none',
        ...sx,
        visibility: state === 'gone' ? 'hidden' : 'visible',
      }}
    >
      <Box
        sx={{
          animation: state === 'popping' ? `faqBubblePop ${POP_MS}ms ease-out forwards` : 'none',
        }}
      >
        <Box
          key={spawn}
          component="img"
          src={state === 'idle' ? bubble : popImg}
          alt=""
          draggable={false}
          onClick={pop}
          sx={{ display: 'block', width: '100%', height: 'auto', pointerEvents: 'auto', ...rise }}
        />
      </Box>
    </Box>
  );
}

// Left-margin offsets that produce the zig-zag in the General / Event Logistics columns.
const generalOffsets = [124, 12, 137, 12, 137, 0];
const logisticsOffsets = [149, 13, 149, 13, 165, 0, 165];

// Every card drifts on its own path at its own speed, like it's floating under water.
// Values come from a seeded hash (not Math.random) so the layout is identical on every
// render. The keyframes (`faqFloat`, in App.css) read these as CSS variables.
const hash = (seed: number, n: number) => {
  const x = Math.sin(seed * 12.9898 + n * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

// Same ambient rise as the Home page's decorative bubbles (`decorativeBubbleRise`, App.css):
// each bubble starts below its resting spot, rises past it while swaying, fades out, and loops.
const bubbleRise = (seed: number, n: number, duration: number, delay = 0) => ({
  animation: `decorativeBubbleRise ${duration}s ease-in-out ${delay}s infinite`,
  '--rise': `${-(50 + hash(seed, n) * 70)}px`,
  '--start': `${20 + hash(seed, n + 1) * 30}px`,
  '--sway': `${6 + hash(seed, n + 2) * 10}px`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const floatFor = (seed: number) => {
  const r = (n: number) => hash(seed, n);
  const px = (n: number, amp: number) => `${((r(n) * 2 - 1) * amp).toFixed(1)}px`;
  return {
    vars: {
      '--fx1': px(1, 7),
      '--fy1': px(2, 6),
      '--fx2': px(3, 9),
      '--fy2': px(4, 7),
      '--fx3': px(5, 7),
      '--fy3': px(6, 6),
    } as CSSProperties,
    duration: 7 + r(7) * 4, // 7–11s
    delay: -r(8) * 10, // start each card at a different point in its loop
  };
};

const FaqCard = ({ q, a, bullets, note, link, seed }: FaqEntry & { seed: number }) => {
  const [open, setOpen] = useState(false);
  // Bulleted answers are the ones long enough to wrap into many lines at the card's normal
  // width, growing the card tall enough to shove everything below it down the page when open.
  // Those are permanently a bit wider (bleeding past the card's own box via negative margins,
  // so it doesn't change how much space the card reserves in the layout, open or closed) — no
  // resizing when it opens, it's just already that size. Short answers are unaffected. 24px
  // total is as far as this can go without overlapping a neighbour — the tightest spot is the
  // "How do I submit my project?" / "Who can participate?" pair, 32px apart at the card's
  // normal width.
  const isLong = Boolean(bullets && bullets.length > 0);
  const uid = useId();
  const panelId = `${uid}-panel`;
  const buttonId = `${uid}-button`;
  const float = floatFor(seed);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // True for the whole close animation (open is still true then, so it can't tell the two
  // apart on its own) — used only to keep the scroll-into-view effect below from re-triggering
  // while the card is shrinking, not growing.
  const closingRef = useRef(false);
  // The answer panel's height in px while it's opening or closing; 'auto' once fully open, so
  // it still reflows if the window is resized.
  const [panelH, setPanelH] = useState<number | 'auto'>(0);
  // How long the grow/shrink takes, scaled to how tall the answer actually is — short answers
  // animate quickly, long ones take a bit longer, capped so nothing drags. Same value, same
  // animation, both directions: opening and closing aren't treated differently.
  const [transitionDuration, setTransitionDuration] = useState(1.2);
  // Bumped on every toggle (open or close) and used as part of the bubble field's key, so React
  // remounts fresh bubble elements each time instead of reusing ones whose one-shot animation
  // already finished — that's what lets the same rise animation replay on closing, not just
  // opening.
  const [transitionNonce, setTransitionNonce] = useState(0);
  // The answer's measured content height, used only to size the bubble field below (see
  // BUBBLE_DENSITY) — set whenever the card opens (and stays put while it's closing, since the
  // content doesn't change size then).
  const [contentHeight, setContentHeight] = useState(0);

  // A dense field of bubbles that rises (opening) or sinks (closing — see the animation
  // direction below) along with the panel, each playing once per transition and done for good
  // once it's finished — this isn't an ambient effect, it's part of the grow/shrink itself. The
  // count scales with the answer's own height (see BUBBLE_DENSITY) so short and long answers
  // both end up proportionally as dense. Each bubble's own rise distance is a random fraction of
  // the full content height — some disappear low, most somewhere in the middle, a few almost at
  // the top — rather than every single one marching up to nearly the same height, which read as
  // oddly uniform. (The overflow clip on the wrapper above is still the hard backstop for the
  // few that roll high regardless.) Delays are biased to start a bit into the transition, not at
  // the very first instant, then stay staggered across the rest of it so they keep emerging the
  // whole time it's animating, before stopping for good. They're anchored (see the transform
  // below) to hang mostly below the panel's current bottom edge, not straddling it evenly, so
  // they appear just under the growing edge and cover it as it moves.
  const answerBubbles = useMemo(() => {
    // Far fewer on phones: the same density in a narrow, tall card is a wall of bubbles.
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 899.95px)').matches;
    const count = isMobile
      ? Math.max(8, Math.round(BUBBLE_DENSITY * contentHeight * 0.25))
      : Math.max(20, Math.round(BUBBLE_DENSITY * contentHeight));
    return Array.from({ length: count }, (_, i) => ({
      left: hash(seed, 900 + i) * 100,
      size: 9 + Math.pow(hash(seed, 950 + i), 1.5) * 17,
      rise: BUBBLE_START_DEPTH + contentHeight * (0.15 + hash(seed, 970 + i) * 0.8),
      duration: 0.7 + hash(seed, 990 + i) * 0.7,
      delayFrac: hash(seed, 1010 + i) * 0.85, // 0.15..1, scaled to transitionDuration below
    }));
  }, [seed, contentHeight]);

  const toggle = () => {
    const panel = panelRef.current;
    const content = contentRef.current;
    if (!panel || !content) {
      setOpen((prev) => !prev);
      return;
    }
    setTransitionNonce((n) => n + 1);
    if (!open) {
      closingRef.current = false;
      const h = content.scrollHeight;
      setTransitionDuration(Math.min(2.2, 1 + h / 400));
      setContentHeight(h);
      setOpen(true);
      setPanelH(h); // 0 -> h, so it visibly grows rather than snapping open
    } else {
      // Measure the current height (it may already be 'auto') and scale the duration the same
      // way opening does, then shrink back to 0 next frame — the same animation as opening,
      // just reversed. `open` itself doesn't flip to false until that shrink actually finishes
      // (see onTransitionEnd below), so the bubble field, icon, etc. stay in their "open" state
      // for the whole collapse instead of snapping shut instantly.
      closingRef.current = true;
      const h = panel.getBoundingClientRect().height;
      setTransitionDuration(Math.min(2.2, 1 + h / 400));
      setPanelH(h);
      requestAnimationFrame(() => setPanelH(0));
    }
  };

  // If the opened card would run off the top or bottom of the screen, scroll so the
  // question and answer sit centred (or, if it's taller than the screen, so the
  // question is at the top and the answer reads downward).
  useEffect(() => {
    if (!open || closingRef.current) return;
    const card = cardRef.current;
    const button = buttonRef.current;
    if (!card || !button) return;

    const nav = document.querySelector('nav');
    const usableTop = (nav ? nav.getBoundingClientRect().height : 75) + 16;
    const usableBottom = window.innerHeight - 16;
    const cardTop = card.getBoundingClientRect().top;
    const finalHeight = button.getBoundingClientRect().height + (typeof panelH === 'number' ? panelH : 0);

    if (cardTop >= usableTop && cardTop + finalHeight <= usableBottom) return; // already fits

    const available = usableBottom - usableTop;
    const desiredTop = finalHeight <= available ? usableTop + (available - finalHeight) / 2 : usableTop;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: cardTop - desiredTop, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [open, panelH]);

  return (
    // Outer box floats. The card inside keeps the hover lift, so the two `translate`
    // animations never fight over one element. It holds still while hovered or open so
    // it's easy to click and read.
    <Box
      style={float.vars}
      sx={{
        width: { xs: '100%', md: CARD_W },
        maxWidth: { xs: 420, md: 'none' },
        animation: `faqFloat ${float.duration}s ease-in-out ${float.delay}s infinite`,
        animationPlayState: open ? 'paused' : 'running',
        '&:hover': { animationPlayState: 'paused' },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
    <Box
      ref={cardRef}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        // At rest (closed, or fully open), one shared cream fill spans the button and the panel
        // as a single paint, same as the original design — no seam between two separately-
        // coloured boxes meeting at a border, which is what a hairline gap between the button's
        // own fill and the panel's own fill turned out to look like. While actively
        // growing/shrinking, this drops out and the panel fills itself in instead (see below),
        // so its colour can fade out at its own growing edge.
        backgroundColor: panelH === 'auto' || panelH === 0 ? '#FCEBD5' : 'transparent',
        borderRadius: '8px',
        // A box-shadow redraws at this box's exact current size every frame, with a crisp edge
        // of its own — no fading, regardless of how soft the fill inside is. So it stays off
        // for as long as the panel is actively growing/shrinking (the one real "edge" that
        // would otherwise still be visible) — i.e. whenever panelH is some in-between number —
        // and only shows at rest: fully closed (0) or fully open ('auto').
        boxShadow:
          panelH === 'auto' || panelH === 0
            ? '0 4px 6px rgba(0, 0, 0, 0.2), 2px 3px 0 rgba(0, 0, 0, 0.06)'
            : 'none',
        transition: 'translate 0.2s ease, box-shadow 0.2s ease',
        '@media (hover: hover) and (prefers-reduced-motion: no-preference)': {
          '&:hover': {
            translate: '0 -3px',
            boxShadow: '0 10px 16px rgba(0, 0, 0, 0.25), 2px 3px 0 rgba(0, 0, 0, 0.06)',
          },
        },
        ...(isLong
          ? { width: { md: 'calc(100% + 24px)' }, ml: { md: '-12px' }, mr: { md: '-12px' } }
          : {}),
      }}
    >
      <Box
        component="button"
        ref={buttonRef}
        id={buttonId}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
        sx={{
          all: 'unset',
          boxSizing: 'border-box',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: '13px',
          py: '17px',
          minHeight: 53,
          cursor: 'pointer',
          backgroundColor: '#FCEBD5',
          // Square at the bottom while open, so it sits flush against the panel below instead
          // of leaving a little rounded notch where two separately-rounded boxes meet.
          borderRadius: open ? '8px 8px 0 0' : '8px',
          fontFamily: fonts.heading,
          fontWeight: 600,
          // Long questions shrink to stay compact, like the design does.
          fontSize: q.length > 55 ? '0.72rem' : q.length > 38 ? '0.8rem' : '0.9rem',
          lineHeight: 1.25,
          color: '#1F1F2E',
          '&:focus-visible': { outline: `2px solid ${colors.navy}`, outlineOffset: 2 },
        }}
      >
        <span>{q}</span>
        <AddIcon
          sx={{
            flexShrink: 0,
            fontSize: '1.1rem',
            // MUI's Add glyph is thin; a stroke matches the design's bolder plus.
            '& path': { stroke: 'currentColor', strokeWidth: 1.6 },
            transform: open ? 'rotate(45deg)' : 'none',
          }}
        />
      </Box>

      {/* The answer panel grows from 0 to its full height as it opens, and shrinks back the same
          way as it closes — same duration, same bubble field, just reversed. A dense field of
          bubbles rises from its bottom edge the whole time it's animating (either direction),
          each playing once and done — not an ambient loop, just part of the transition itself. */}
      <Box
        ref={panelRef}
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        style={{ height: panelH }}
        onTransitionEnd={(e) => {
          if (e.target !== e.currentTarget || e.propertyName !== 'height') return;
          // Which direction just finished is read off the actual resulting height rather than
          // `open` (which now stays true for the whole close animation, not just the open one):
          // landing on a real height means the open grow just finished (switch to 'auto' so it
          // still reflows on resize); landing on 0 means the close shrink just finished (now
          // it's safe to flip `open` false for good).
          const finished = panelRef.current?.getBoundingClientRect().height ?? 0;
          if (finished > 0) {
            setPanelH('auto');
          } else {
            setOpen(false);
            setPanelH(0);
          }
        }}
        sx={{
          position: 'relative',
          // No overflow clipping on this box itself — the bubbles below deliberately hang
          // past its bottom edge, and clipping here would cut them off right where they're
          // meant to be most visible. The text below has its own clipping wrapper instead.
          transition: `height ${transitionDuration}s ease-out`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          // This box's own fill: while mid-transition, the cream fades into the FAQ section's
          // periwinkle over the last EDGE_FADE_LENGTH px, so the growing edge dissolves into the
          // page rather than ending in a hard-edged cream rectangle. Once panelH settles to
          // 'auto', this goes transparent and cardRef's own shared fill (above) shows through
          // instead — one continuous paint with the button, no seam between two separately
          // coloured boxes that happen to share a border.
          background:
            panelH === 'auto'
              ? 'transparent'
              : `linear-gradient(to bottom, #FCEBD5 0%, #FCEBD5 calc(100% - ${EDGE_FADE_LENGTH}px), #8FA6E8 100%)`,
          borderRadius: panelH === 'auto' ? '0 0 8px 8px' : 0,
        }}
      >
        {/* This layer spans from the panel's own top (fixed — only the bottom edge moves as it
            grows) down to BUBBLE_START_DEPTH below its current bottom, so it tracks the growing
            edge as it opens the same way `inset: 0` would, but with that extra bit of overhang
            below for bubbles to start from. Clipped at the top only (see the clip-path below) so
            nothing can rise past the panel's actual top, whatever any individual bubble's own
            rise distance happens to be — a hard backstop on top of the keyframe's own fade-out,
            not instead of it — while bubbles extending past any other edge are left alone. Keyed
            on transitionNonce so React mounts a fresh set of bubbles (with their one-shot
            animation un-played) on every toggle, instead of reusing ones that already finished
            rising during a previous open. */}
        {open && (
          <Box
            key={transitionNonce}
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: -BUBBLE_START_DEPTH,
              // Width is still the normal card width (that hasn't changed) — only the top edge
              // is actually clipped, to back-stop bubbles rising past it. `overflow-x: visible`
              // paired with `overflow-y: hidden` doesn't work for this — per spec, a 'visible'
              // on one axis computes to 'auto' (which still visually clips at rest) once the
              // other axis is non-visible. clip-path doesn't have that restriction: an inset()
              // with huge negative left/right/bottom values pushes those clip edges far outside
              // any bubble's reach, leaving only the top (inset at the real 0) actually clipping
              // anything — so bubbles that extend past the left/right edges (or hang below the
              // bottom, as before) are left alone and bleed outside it.
              clipPath: 'inset(0px -9999px -9999px -9999px)',
              WebkitClipPath: 'inset(0px -9999px -9999px -9999px)',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            {answerBubbles.map((b, i) => (
              <Box
                key={i}
                component="img"
                src={bubble}
                alt=""
                style={{ '--rise': `${-b.rise}px` } as CSSProperties}
                sx={{
                  position: 'absolute',
                  left: `${b.left}%`,
                  // The wrapper's own bottom edge is already sunk BUBBLE_START_DEPTH below the
                  // panel's real edge (see above), so sitting at this box's own bottom (0) is
                  // exactly that sunken anchor point.
                  bottom: 0,
                  width: b.size,
                  height: b.size,
                  // Centred on that sunken anchor point (not straddling the real edge), so the
                  // bubble reads as rising up from below and covering the edge as it passes it.
                  transform: 'translate(-50%, 50%)',
                  animation: `faqAnswerBubbleRise ${b.duration}s ease-out ${b.delayFrac * transitionDuration}s 1 both`,
                  '@media (prefers-reduced-motion: reduce)': { display: 'none' },
                }}
              />
            ))}
          </Box>
        )}

        {/* Clips the text to the panel's current height (so it's still progressively revealed
            as the panel grows) without clipping the bubbles above, which live outside this
            wrapper for exactly that reason. While still growing (or shrinking), a mask fades
            the last EDGE_FADE_LENGTH px of that revealed sliver to transparent, matching the
            background gradient above, so the edge reads as dissolving into the bubbles rather
            than a hard cut. Only while mid-transition: once panelH settles to 'auto', the mask
            drops and the full answer reads normally. */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
            height: '100%',
            ...(panelH !== 'auto'
              ? {
                  maskImage: `linear-gradient(to bottom, black calc(100% - ${EDGE_FADE_LENGTH}px), transparent 100%)`,
                  WebkitMaskImage: `linear-gradient(to bottom, black calc(100% - ${EDGE_FADE_LENGTH}px), transparent 100%)`,
                }
              : {}),
          }}
        >
        <Box
          ref={contentRef}
          sx={{
            px: '13px',
            pt: '2px',
            fontFamily: fonts.heading,
            fontWeight: 500,
            fontSize: '0.82rem',
            lineHeight: 1.45,
            color: '#3a3a4d',
          }}
        >
          {/* The theme's body1 style is bold/large, so plain <p>/<ul> keep this readable. */}
          {a && <Box component="p" sx={{ m: 0, pb: '10px' }}>{a}</Box>}
          {bullets && (
            <Box component="ul" sx={{ m: 0, pl: '1.1rem', pb: '10px', listStyle: 'disc' }}>
              {bullets.map((item) => {
                const text = typeof item === 'string' ? item : item.text;
                const sub = typeof item === 'string' ? undefined : item.sub;
                return (
                  <Box
                    component="li"
                    key={text}
                    sx={{ mb: '6px', '&::marker': { color: colors.navy } }}
                  >
                    {text}
                      {sub && (
                        <Box component="ul" sx={{ m: 0, mt: '4px', pl: '1rem', listStyle: 'circle' }}>
                          {sub.map((subItem) => (
                            <Box component="li" key={subItem} sx={{ mb: '3px' }}>
                              {subItem}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          {note && <Box component="p" sx={{ m: 0, pb: '10px', fontWeight: 700 }}>{note}</Box>}
          {link && (
            <Box component="p" sx={{ m: 0, pb: '10px' }}>
              <Box
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: colors.navy, fontWeight: 700, textDecoration: 'underline' }}
              >
                {link.label}
              </Box>
            </Box>
          )}
        </Box>
        </Box>
      </Box>
    </Box>
    </Box>
  );
};

const groupHeadingSx = {
  fontFamily: fonts.heading,
  fontWeight: 700,
  fontSize: { xs: '1.8rem', md: '2.4rem' },
  lineHeight: 1.15,
  color: '#B9E5EE',
  textShadow: '0px 3px 4px rgba(0, 0, 0, 0.25)',
} as const;

const FAQ = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Title, headings and cards rise in as they scroll into view.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('.faq-reveal').forEach((el) => {
        gsap.set(el, { opacity: 0, y: 36 });
        gsap.to(el, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 92%' },
        });
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <Box
      component="section"
      id="faq"
      ref={rootRef}
      sx={{
        position: 'relative',
        // Kept small — the dedicated Schedule -> FAQ bubble strip (Home.tsx) right above this
        // section already provides the bulk of the visual transition/breathing room, so this
        // only needs a little space before the title, not a second helping of it.
        pt: { xs: 4, md: 6 },
        pb: { xs: 4, md: 20 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}
    >
      {/* Soft periwinkle wash — solid top to bottom, no fade at either edge. Both neighbouring
          transition strips (Home.tsx) pick up these same colours (#88A3EA at the top, #8FA6E8
          at the bottom) and gradient on from there, so each colour hand-off is explicit instead
          of relying on a fade to reveal the page's global background gradient underneath. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          // Stays a light periwinkle blue top to bottom (no drift toward lavender).
          background: 'linear-gradient(180deg, #88A3EA 0%, #8FA6E8 100%)',
        }}
      />

      {/* Decorative bubbles */}
      {[
        { sx: { left: '7.5%', top: '5%', width: 'min(88px, 7vw)' }, d: bubbleRise(603, 0, 6, 0) },
        { sx: { left: '5.3%', top: '15.7%', width: 'min(50px, 4vw)' }, d: bubbleRise(603, 10, 5, 1.2) },
        { sx: { right: '2.7%', top: '9.5%', width: 'min(50px, 4vw)' }, d: bubbleRise(603, 20, 5.5, 0.6) },
        { sx: { right: '5.7%', top: '16.5%', width: 'min(88px, 7vw)' }, d: bubbleRise(603, 30, 7, 2) },
      ].map((b, i) => (
        <PoppableBubble key={i} sx={b.sx} rise={b.d} />
      ))}

      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1140, mx: 'auto' }}>
        <Typography
          className="faq-reveal"
          component="h2"
          sx={{
            fontFamily: fonts.display,
            fontWeight: 700,
            fontSize: { xs: '3rem', md: '4.5rem' },
            lineHeight: 1,
            color: colors.navy,
            textAlign: 'center',
            mb: { xs: 4, md: 5 },
          }}
        >
          FAQ
        </Typography>

        {/* Registration & Participation — centred, 2 x 2 staggered */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            className="faq-reveal"
            component="h3"
            sx={{ ...groupHeadingSx, textAlign: 'center', mb: { xs: 3, md: 6 } }}
          >
            Registration &amp; Participation
          </Typography>

          <Box
            sx={{
              width: { md: 590 },
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: '24px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, gap: { xs: '16px', md: '25px' }, width: '100%' }}>
              <Box className="faq-reveal" sx={{ width: { xs: '100%', md: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                <FaqCard {...registration[0]} seed={0} />
              </Box>
              <Box className="faq-reveal" sx={{ mt: { md: '26px' }, width: { xs: '100%', md: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                <FaqCard {...registration[1]} seed={1} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, gap: { xs: '16px', md: '0px' }, width: '100%' }}>
              <Box className="faq-reveal" sx={{ ml: { md: '44px' }, width: { xs: '100%', md: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                <FaqCard {...registration[2]} seed={2} />
              </Box>
              <Box className="faq-reveal" sx={{ ml: { md: '82px' }, mt: { md: '6px' }, width: { xs: '100%', md: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                <FaqCard {...registration[3]} seed={3} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* General (left) and Event Logistics (right) */}
        <Box
          sx={{
            mt: { xs: 8, md: 9 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'flex-start' },
            gap: { xs: 8, md: 4 },
          }}
        >
          <Box sx={{ width: { xs: '100%', md: 371 }, display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'stretch' } }}>
            <Typography
              className="faq-reveal"
              component="h3"
              sx={{ ...groupHeadingSx, ml: { md: '143px' }, textAlign: { xs: 'center', md: 'left' }, mb: { xs: 3, md: '38px' } }}
            >
              General
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, gap: { xs: '16px', md: '30px' }, width: '100%' }}>
              {general.map((item, i) => (
                <Box key={item.q} className="faq-reveal" sx={{ ml: { md: `${generalOffsets[i]}px` }, width: { xs: '100%', md: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                  <FaqCard {...item} seed={10 + i} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ width: { xs: '100%', md: 397 }, mt: { md: '100px' }, display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'stretch' } }}>
            <Typography
              className="faq-reveal"
              component="h3"
              sx={{ ...groupHeadingSx, ml: { md: '87px' }, textAlign: { xs: 'center', md: 'left' }, mb: { xs: 3, md: '30px' } }}
            >
              Event Logistics
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, gap: { xs: '16px', md: '26px' }, width: '100%' }}>
              {logistics.map((item, i) => (
                <Box key={item.q} className="faq-reveal" sx={{ ml: { md: `${logisticsOffsets[i]}px` }, width: { xs: '100%', md: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                  <FaqCard {...item} seed={20 + i} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FAQ;
